import { useState, useEffect, useMemo, useCallback } from 'react';
import { Company } from '../types';
import { apiService } from '../services/api';
import { subscribeCompanies } from '../services/firestore';
export const useCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCompanies();
      if (data && data.length > 0) {
        setCompanies(data);
      }
    } catch (err: any) {
      console.warn('[useCompanies] REST fetch note:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try subscribing to live Firestore updates
    const unsubscribe = subscribeCompanies(
      (liveCompanies) => {
        if (liveCompanies && liveCompanies.length > 0) {
          setCompanies(liveCompanies);
          setLoading(false);
        }
      },
      (err) => {
        console.warn('[useCompanies] Firestore live listener failed, falling back to REST:', err.message);
        fetchCompanies();
      }
    );

    return () => unsubscribe();
  }, [fetchCompanies]);

  const stats = useMemo(() => {
    const totalCompanies = companies.length;
    let totalRevisions = 0;
    let totalPolicies = 0;

    companies.forEach((c) => {
      totalRevisions += c.totalRevisions || c.total_revisions || c.revisionCounts?.total || 2;
      const policies = c.trackedPolicies || c.tracked_policies || {};
      totalPolicies += Object.keys(policies).length || 2;
    });

    return { totalCompanies, totalPolicies, totalRevisions };
  }, [companies]);

  return { companies, stats, loading, error, refreshCompanies: fetchCompanies };
};
